import { z } from 'zod';
import { themeTypeSchema } from './kg-insights';

const earningsInsightsBasePath = 'earnings-insights/v1/';
const earningsInsightsV4BasePath = 'earnings-insights/v4/';

const otherThemeTypeSchema = z.enum(['Macro', 'Financial']);
const earningsInsightTypeSchema = z.enum(['preview', 'recap']);
export type EarningsInsightType = z.infer<typeof earningsInsightTypeSchema>;

export const ratingSchema = z.enum(['positive', 'neutral', 'negative']);
export type EarningsInsightRating = z.infer<typeof ratingSchema>;

const baseInsightSchema = z.object({
  id: z.string(),
  type: earningsInsightTypeSchema,
  entity_tag: z.string(),
  earnings_id: z.string(),
  title: z.string(),
  created_at: z.string(),
  updated_at: z.string(),
});

const baseOutlookSchema = z.object({
  base_case: z.string(),
  bear_case: z.string(),
  bull_case: z.string(),
});

const baseThemeSchema = z.object({
  rating: ratingSchema,
  relationship: z.string(),
});

const earningsInsightSchema = baseInsightSchema;
const earningsInsightFilterResponseSchema = z.array(earningsInsightSchema);
const earningsInsightV4Schema = earningsInsightSchema.extend({
  ric: z.string(),
  updated_at: z.string().optional(),
});
const earningsInsightFilterV4ResponseSchema = z.array(earningsInsightV4Schema);

export type EarningsInsight = z.infer<typeof earningsInsightSchema>;
export type EarningsInsightFilterResponse = z.infer<
  typeof earningsInsightFilterResponseSchema
>;
export type EarningsInsightFilterV4Response = z.infer<
  typeof earningsInsightFilterV4ResponseSchema
>;

const earningsInsightsFilterRequestSchema = z.object({
  entities: z.array(z.string()).optional(),
  earnings_ids: z.array(z.string()).optional(),
  types: z.array(earningsInsightTypeSchema).optional(),
  from: z.string().optional(),
  to: z.string().optional(),
  page: z.number().optional(),
  page_size: z.number().optional(),
});

export type EarningsInsightsFilterRequest = z.infer<
  typeof earningsInsightsFilterRequestSchema
>;

const kpiSchema = z.object({
  title: z.string(),
  description: z.string(),
  value: z.string(),
  rating: ratingSchema.optional(),
});
export type RecapKpi = z.infer<typeof kpiSchema>;

export const themeSchema = baseThemeSchema.extend({
  theme_id: z.string(),
});
export type Theme = z.infer<typeof themeSchema>;

const earningsThemeV4Schema = themeSchema.extend({
  type: themeTypeSchema,
});

export const otherThemeSchema = baseThemeSchema.extend({
  name: z.string(),
  type: otherThemeTypeSchema,
});
export type OtherTheme = z.infer<typeof otherThemeSchema>;

const investmentOutlookSchema = baseOutlookSchema;

const impactedCompanySchema = z.object({
  entity_tag: z.string(),
  earnings_id: z.string().optional(),
  rating: ratingSchema,
  description: z.string(),
  relationship: z.string(),
});
export type ImpactedCompany = z.infer<typeof impactedCompanySchema>;
const impactedCompanyV4Schema = impactedCompanySchema.extend({
  ric: z.string(),
  earnings_id: z.string(),
});

export const insightCitationSchema = z.object({
  title: z.string(),
  url: z.string(),
});

const earningsInsightPreviewResponseSchema = baseInsightSchema.extend({
  expectation: z.string(),
  historical_context: z.array(z.string()),
  seasonality: z.array(z.string()),
  kpis: z.array(kpiSchema),
  themes: z.array(themeSchema),
  other_themes: z.array(otherThemeSchema),
  key_items: z.array(z.string()),
  investment_outlook: investmentOutlookSchema,
  impacted_companies: z.array(impactedCompanySchema),
  citations: z.array(insightCitationSchema),
});
export type EarningsInsightPreviewResponse = z.infer<
  typeof earningsInsightPreviewResponseSchema
>;
const earningsInsightPreviewV4ResponseSchema =
  earningsInsightPreviewResponseSchema.omit({ other_themes: true }).extend({
    ...earningsInsightV4Schema.shape,
    type: z.literal('preview'),
    themes: z.array(earningsThemeV4Schema),
    impacted_companies: z.array(impactedCompanyV4Schema),
  });
export type EarningsInsightPreviewV4Response = z.infer<
  typeof earningsInsightPreviewV4ResponseSchema
>;

const insightSchema = z.object({
  title: z.string(),
  description: z.string(),
  rating: ratingSchema,
});
export type ManagementInsight = z.infer<typeof insightSchema>;

const quoteSchema = z.object({
  context: z.string(),
  text: z.string(),
  section_id: z.string(),
});

export type ManagementQuote = z.infer<typeof quoteSchema>;

const analystReactionSchema = baseOutlookSchema;

const riskAssessmentSchema = z.object({
  assessments: z.array(z.string()),
  upcoming_catalysts: z.string(),
});

const documentSchema = z.object({
  id: z.string(),
  name: z.string(),
});

const earningsInsightRecapResponseSchema = baseInsightSchema.extend({
  key_metrics: z.string(),
  guidance_update: z.string(),
  reaction_desc: z.string(),
  kpis: z.array(kpiSchema),
  insights: z.array(insightSchema),
  quotes: z.array(quoteSchema),
  themes: z.array(themeSchema),
  other_themes: z.array(otherThemeSchema),
  impacted_companies: z.array(impactedCompanySchema),
  peer_comparison_sector_context: z.array(z.string()),
  analyst_and_street_reaction: analystReactionSchema,
  risk_assessment_update: riskAssessmentSchema,
  citations: z.array(insightCitationSchema),
  documents: z.array(documentSchema),
});
export type EarningsInsightRecapResponse = z.infer<
  typeof earningsInsightRecapResponseSchema
>;
const earningsInsightRecapV4ResponseSchema = earningsInsightRecapResponseSchema
  .omit({ other_themes: true })
  .extend({
    ...earningsInsightV4Schema.shape,
    type: z.literal('recap'),
    kpis: z.array(
      kpiSchema.extend({
        value: z.string().optional(),
        rating: ratingSchema,
      })
    ),
    themes: z.array(earningsThemeV4Schema),
    impacted_companies: z.array(impactedCompanyV4Schema),
    market_reactions: z.array(z.string()),
  });
export type EarningsInsightRecapV4Response = z.infer<
  typeof earningsInsightRecapV4ResponseSchema
>;

export const earningsInsights = {
  postEarningsInsightFilter: {
    path: `${earningsInsightsBasePath}filter`,
    schema: earningsInsightFilterResponseSchema,
  },
  getEarningsInsightPreview: {
    path: (insightId: string) =>
      `${earningsInsightsBasePath}preview/${insightId}`,
    schema: earningsInsightPreviewResponseSchema,
  },
  getEarningsInsightRecap: {
    path: (insightId: string) =>
      `${earningsInsightsBasePath}recap/${insightId}`,
    schema: earningsInsightRecapResponseSchema,
  },
  postEarningsInsightFilterV4: {
    path: `${earningsInsightsV4BasePath}filter`,
    schema: earningsInsightFilterV4ResponseSchema,
  },
  getEarningsInsightPreviewV4: {
    path: (insightId: string) =>
      `${earningsInsightsV4BasePath}preview/${insightId}`,
    schema: earningsInsightPreviewV4ResponseSchema,
  },
  getEarningsInsightRecapV4: {
    path: (insightId: string) =>
      `${earningsInsightsV4BasePath}recap/${insightId}`,
    schema: earningsInsightRecapV4ResponseSchema,
  },
};
